// WB-M1B S5 (doc 23 §27) -- where a NEW equation goes, and at what scale, when
// the caller of `insertEquation` names neither. Pure: numbers in, numbers
// out.
//
// Two things a phone showed (doc 23 §24, §26):
//   - a formula wider than the visible board was inserted with both ends out
//     of sight;
//   - every new equation landed on the same point, on top of the last one.
//
// Both are decided HERE, once, at insertion. What comes out is an ordinary
// `em` (inside the approved range) and an ordinary position: the same carrier
// as before is saved, and a reload gives the same picture. Nothing is
// remembered between inserts -- the only input is the board as it is. An
// equation that is already on the board is never moved or rescaled by this,
// on whatever screen it is opened later.

import {
  BOARD_EQUATION_DEFAULT_EM,
  BOARD_EQUATION_EM_MAX,
  BOARD_EQUATION_EM_MIN,
} from './boardEquationGeometry'

// Kept free between a new equation and the edge of the visible board, in
// screen px.
export const BOARD_EQUATION_INSERT_MARGIN_PX = 12

// The smallest scale a new equation is shrunk to in order to fit, in screen
// px per em: half the default. At that size a formula's letters are about
// 5 px high; smaller is past reading. A formula too long even for that keeps
// this scale and is placed with its START in view.
export const BOARD_EQUATION_INSERT_MIN_EM_PX = BOARD_EQUATION_DEFAULT_EM / 2

// One step of the cascade, in screen px.
export const BOARD_EQUATION_INSERT_STEP_PX = Object.freeze({ x: 16, y: 32 })

function positive(value) {
  return typeof value === 'number' && Number.isFinite(value) && value > 0
}

function finite(value) {
  return typeof value === 'number' && Number.isFinite(value)
}

// The centre a box of `size` gets along one axis so that it stays inside
// [from, from + length]; a box longer than that starts at `from`.
function hold(center, size, from, length) {
  if (size >= length) return from + size / 2
  return Math.min(Math.max(center, from + size / 2), from + length - size / 2)
}

/**
 * The scale a new equation is inserted at: the default, or -- when the
 * picture would not fit the room at the default -- the largest scale at
 * which all of it does, to two decimals, never below the readable minimum.
 * The natural aspect ratio is not touched: only `em` changes.
 * @param {{widthEm: number, heightEm: number}} natural -- the renderer's
 *   picture
 * @param {{width: number, height: number}} room -- scene px
 * @param {number} zoom -- the editor's zoom (screen px per scene px)
 * @returns {number}
 */
export function equationFitEm(natural, room, zoom) {
  const em = BOARD_EQUATION_DEFAULT_EM
  if (
    !natural ||
    !positive(natural.widthEm) ||
    !positive(natural.heightEm) ||
    !room ||
    !positive(room.width) ||
    !positive(room.height)
  ) {
    return em
  }
  const fit = Math.min(
    room.width / natural.widthEm,
    room.height / natural.heightEm
  )
  if (fit >= em) return em
  const least = Math.min(
    em,
    Math.max(
      BOARD_EQUATION_EM_MIN,
      BOARD_EQUATION_INSERT_MIN_EM_PX / (positive(zoom) ? zoom : 1)
    )
  )
  return Math.max(least, Math.floor(fit * 100) / 100)
}

/**
 * Where a new equation goes and at what scale.
 *
 * Scale: `em` when the caller gives one (kept inside the range), otherwise
 * `equationFitEm` for the visible board.
 *
 * Position: the middle of the visible board; when an equation is already
 * there, one step down and to the right, and so on while the picture still
 * moves down inside the visible board -- then the middle again. The picture
 * is kept inside the visible board; one longer than the board starts at its
 * left (top) edge.
 * @param {{natural: {widthEm: number, heightEm: number}, em?: number,
 *   visible: {x: number, y: number, width: number, height: number,
 *   zoom: number}, taken?: ReadonlyArray<{x: number, y: number}>}} spec --
 *   `visible`: the visible board in scene coordinates and the editor's zoom;
 *   `taken`: the centres of the equations that are on the board
 * @returns {{em: number, center: {x: number, y: number}}}
 */
export function equationInsertPlacement({ natural, em, visible, taken }) {
  const given = finite(em)
  const fallback = given
    ? Math.min(BOARD_EQUATION_EM_MAX, Math.max(BOARD_EQUATION_EM_MIN, em))
    : BOARD_EQUATION_DEFAULT_EM
  const view = visible || {}
  const origin = {
    x: finite(view.x) ? view.x : 0,
    y: finite(view.y) ? view.y : 0,
  }
  // No usable view (an editor that has no size yet): the plain default.
  if (!positive(view.width) || !positive(view.height)) {
    return { em: fallback, center: origin }
  }
  const zoom = positive(view.zoom) ? view.zoom : 1
  const margin = Math.min(
    BOARD_EQUATION_INSERT_MARGIN_PX / zoom,
    view.width / 4,
    view.height / 4
  )
  const room = {
    x: origin.x + margin,
    y: origin.y + margin,
    width: view.width - 2 * margin,
    height: view.height - 2 * margin,
  }
  const scale = given ? fallback : equationFitEm(natural, room, zoom)
  const width = natural.widthEm * scale
  const height = natural.heightEm * scale
  const middle = {
    x: origin.x + view.width / 2,
    y: origin.y + view.height / 2,
  }
  const step = {
    x: BOARD_EQUATION_INSERT_STEP_PX.x / zoom,
    y: BOARD_EQUATION_INSERT_STEP_PX.y / zoom,
  }
  const at = index => ({
    x: hold(middle.x + index * step.x, width, room.x, room.width),
    y: hold(middle.y + index * step.y, height, room.y, room.height),
  })
  // A place is taken when an equation's centre is on its row of the visible
  // board.
  const others = (taken || []).filter(
    point => point && finite(point.x) && finite(point.y)
  )
  const isFree = place =>
    !others.some(
      point =>
        Math.abs(point.y - place.y) < step.y / 2 &&
        Math.abs(point.x - place.x) < view.width / 2
    )
  // The steps at which the picture still moves down inside the room.
  const last = Math.max(
    0,
    Math.floor((room.y + room.height - height / 2 - middle.y) / step.y)
  )
  for (let index = 0; index <= last; index += 1) {
    const place = at(index)
    if (isFree(place)) return { em: scale, center: place }
  }
  return { em: scale, center: at(0) }
}
